import type { Animation } from "@pdfking/book3d"
import { Separator } from "@pdfking/components"
import { Abs, Max, Min, POS_INFINITY } from "@pdfking/math"
import { CreateRenderFunction, NextFrame } from "@pdfking/reactivity/preact"
import type { PlotMedia } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"
import type { ComponentChildren } from "preact"

import type { Guide, LazyPage } from "#public"
import type { PageGrouping } from "#public"

import { GuideToggles } from "../components/GuideToggles"
import { Pager } from "../components/Pager"
import { ToggleButton } from "../components/ToggleButton"
import { ViewerHeader } from "../components/ViewerHeader"
import { ZoomControl } from "../components/ZoomControl"
import { PlotMixin } from "../mixins/PlotMixin"
import { RootMixin } from "../mixins/RootMixin"
import { ComputePageBox, SpreadSideAt } from "./PageBox"
import { PlotStack } from "./PlotStack"

export const PlotViewer = CreateRenderFunction({
  computed: {
    // Each page's own SVG now carries its bleed + spacing margins, so the group
    // footprint the zoom fits already includes that padding — no extra factor.
    base_scale() {
      const { width, height } = this.max_group_size
      if (!width || !height || !this.container_width || !this.container_height) return 1
      return Min(this.container_width / width, this.container_height / height)
    },
    // The actual CSS `zoom` factor applied to the page stack.
    effective_zoom() {
      return this.base_scale / (this.zoom || 1)
    },
    // Pages partitioned into scroll units (see PlotGroup): one page per group
    // ("single"), or side-by-side spreads with the cover kept alone ("cover")
    // or paired from the first page ("double"). Everything downstream — zoom
    // fit, centring padding, scroll target — treats the group as the unit.
    groups(): LazyPage[][] {
      if (this.page_grouping === "single") return this.pages.map((page: LazyPage) => [page])
      const result: LazyPage[][] = []
      let i = 0
      if (this.page_grouping === "cover" && this.pages.length) {
        result.push([this.pages[0]])
        i = 1
      }
      for (; i < this.pages.length; i += 2) result.push(this.pages.slice(i, i + 2))
      return result
    },
    // Global (0-based) index of each group's first page, so a group can label
    // its pages with stable page-numbered ids for scroll/detection.
    group_starts(): number[] {
      const starts: number[] = []
      let start = 0
      for (const group of this.groups) {
        starts.push(start)
        start += group.length
      }
      return starts
    },
    // Group index that owns each page (indexed by page number − 1), so
    // navigating to a page scrolls its whole group into view.
    page_to_group(): number[] {
      const map: number[] = []
      this.groups.forEach((group: LazyPage[], gi: number) => group.forEach(() => map.push(gi)))
      return map
    },
    // The 100%-zoom unit is the group (a spread is as wide as its two pages),
    // so the fit is measured on the largest group, not the largest page.
    max_group_size() {
      let max_width = 0
      let max_height = 0
      for (const group of this.groups) {
        const w = this.group_width(group)
        const h = this.group_height(group)
        if (w > max_width) max_width = w
        if (h > max_height) max_height = h
      }
      return { height: max_height, width: max_width }
    },
    // Whether pages flow in a row (stripe) or a column (normal).
    stripe() {
      return this.animation === "stripe"
    },
    // Extra space (in unzoomed page px, since it sits inside the `zoom`ed
    // stack) so the first and last page can be scrolled all the way to the
    // viewport centre instead of being pinned against the scroll start/end —
    // and so the single row/column is centred on its cross axis too. Divide
    // the container by `effective_zoom` to read the viewport in the same
    // coordinate space the pages are laid out in, so it stays correct at any
    // zoom. `lead`/`trail` are sized per end-page (an odd-sized cover still
    // centres); `cross` fills the viewport so `items-center` does the rest.
    center_padding() {
      const ez = this.effective_zoom
      const first = this.groups[0]
      const last = this.groups[this.groups.length - 1]
      if (!ez || !first || !last) return { cross: 0, lead: 0, trail: 0 }
      const main = (this.stripe ? this.container_width : this.container_height) / ez
      const half = (group: LazyPage[]) =>
        Max(0, (main - (this.stripe ? this.group_width(group) : this.group_height(group))) / 2)
      return {
        cross: (this.stripe ? this.container_height : this.container_width) / ez,
        lead: half(first),
        trail: half(last),
      }
    },
  },
  data: {
    container_height: 0,
    container_width: 0,
    // Last page reported to `on_current_page` — guards the scroll → prop →
    // scrollIntoView feedback loop.
    detected_page: 1,
    resize_observer: undefined as ResizeObserver | undefined,
    // rAF guard so a scroll burst measures page positions once, not per event.
    scroll_scheduled: false,
  },
  methods: {
    page_element(n: number): HTMLElement | null {
      return document.getElementById(`${this.uuid}-${n - 1}`)
    },
    group_element(gi: number): HTMLElement | null {
      return document.getElementById(`${this.uuid}-g-${gi}`)
    },
    // A page's full element box, clamped like PlotGroup renders it (inner edges
    // of a spread lose their margin), so the fit/centre maths measure exactly
    // what ends up on screen.
    page_box(group: LazyPage[], i: number) {
      return ComputePageBox(
        group[i],
        { bleed: this.bleed, side: SpreadSideAt(i, group.length) },
        this.to_px
      )
    },
    group_width(group: LazyPage[]): number {
      let sum = 0
      for (let i = 0; i < group.length; i++) sum += this.page_box(group, i).width
      return sum
    },
    group_height(group: LazyPage[]): number {
      let max = 0
      for (let i = 0; i < group.length; i++) max = Max(max, this.page_box(group, i).height)
      return max
    },
    // The scroll viewport (the plot area beneath the header) — what we measure
    // and observe, since `this.root` now also spans the header.
    reader(): HTMLElement | undefined {
      return this.root?.querySelector<HTMLElement>('[data-ref="reader"]') ?? undefined
    },
    // Scroll fires far faster than we can afford 129 layout reads, so coalesce
    // every burst into one measurement on the next frame.
    on_scroll() {
      if (this.scroll_scheduled) return
      this.scroll_scheduled = true
      NextFrame(() => {
        this.scroll_scheduled = false
        this.detect_page()
      })
    },
    // Reports the page whose centre (along the scroll axis — vertical normally,
    // horizontal in stripe mode) sits closest to the viewport's own centre.
    // Navigation centres the same way (see `scroll_to_page`), so a detected
    // page and a requested one agree instead of fighting over the feedback loop.
    detect_page() {
      const reader = this.reader()
      if (!reader || !this.pages.length) return
      const reader_rect = reader.getBoundingClientRect()
      const center = this.stripe
        ? reader_rect.left + reader_rect.width / 2
        : reader_rect.top + reader_rect.height / 2
      let best = 1
      let best_distance = POS_INFINITY
      for (let i = 0; i < this.pages.length; i++) {
        const el = this.page_element(i + 1)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        const point = this.stripe ? rect.left + rect.width / 2 : rect.top + rect.height / 2
        const distance = Abs(point - center)
        if (distance < best_distance) {
          best_distance = distance
          best = i + 1
        }
      }
      if (best !== this.detected_page) {
        this.detected_page = best
        this.on_current_page?.(best)
      }
    },
    // Centres a page on both axes by scrolling the exact delta between the
    // page centre and the viewport centre. Both come from `getBoundingClientRect`,
    // so the reading already accounts for the `zoom` on the stack — unlike
    // `scrollIntoView`, which miscomputes offsets inside a zoomed element. The
    // browser clamps out-of-range targets, and the leading/trailing
    // `center_padding` is what lets the first/last page reach the centre.
    scroll_to_page(n: number, behavior: ScrollBehavior = "auto") {
      const reader = this.reader()
      // Centre the whole group (spread), not the bare page — the group is the
      // scroll unit. Fall back to the page if the group isn't mapped yet.
      const gi = this.page_to_group[n - 1]
      const el = NullQ(gi) ? this.page_element(n) : this.group_element(gi)
      if (!reader || !el) return
      const view = reader.getBoundingClientRect()
      const page = el.getBoundingClientRect()
      reader.scrollTo({
        behavior,
        left: reader.scrollLeft + (page.left + page.width / 2) - (view.left + view.width / 2),
        top: reader.scrollTop + (page.top + page.height / 2) - (view.top + view.height / 2),
      })
    },
    resize() {
      const reader = this.reader()
      this.container_width = reader?.clientWidth ?? 0
      this.container_height = reader?.clientHeight ?? 0
    },
  },
  mixins: [PlotMixin, RootMixin] as const,
  mounted() {
    // Adopt the URL's page before measuring: `resize()` fills the container
    // size, which flips `effective_zoom` off its zero-state and fires the
    // re-centre watcher — it must already know which page to centre. (The
    // `current_page` watcher never sees the initial value, only changes.)
    if (this.current_page > 1) this.detected_page = this.current_page
    this.resize()
    const reader = this.reader()
    this.resize_observer = new ResizeObserver(this.resize)
    if (reader) this.resize_observer.observe(reader)
  },
  props: {
    active_guides: [] as Guide[],
    allow_selection: true,
    // The page layout — "normal" stacks pages in a column, "stripe" lays them
    // out in a row, same toggle as the 3D book's flat top-down view.
    animation: "normal" as Animation,
    bleed: false,
    // Externally requested page (1-based); changing it scrolls that page into
    // view. Scroll-driven changes report through `on_current_page` instead.
    current_page: 0,
    // The full guide list + hidden set for the header's guide toggles (the page
    // render uses the pre-filtered `active_guides` above instead).
    guides: [] as Guide[],
    // See PdfViewerOptions.show_header.
    show_header: true,
    // The host's verbatim right-side header controls (download).
    header_actions: undefined as ComponentChildren,
    // The host's control for the header's corner over the rail (3D switch).
    header_corner: undefined as ComponentChildren,
    hidden_guides: [] as string[],
    // Only read by the translation hooks, but declared so a language switch
    // re-renders the pager labels.
    lang: "en",
    // The host's finished layout buttons (page pairing, stripe toggle) for the
    // left header group, rendered verbatim (see CreatePdfViewer).
    layout_controls: undefined as ComponentChildren,
    media_count: 0,
    on_current_page: undefined as ((n: number) => void) | undefined,
    on_page_clicked: undefined as ((info: { number: number; page: PlotMedia }) => void) | undefined,
    on_toggle_bleed: undefined as (() => void) | undefined,
    on_toggle_guide: undefined as ((id: string) => void) | undefined,
    on_zoom: undefined as ((zoom: number) => void) | undefined,
    // The 2D page-pairing mode (single / cover / double).
    page_grouping: "single" as PageGrouping,
    pages: [] as LazyPage[],
    // The host's rail and open panel (see ViewerSidebar), laid out under the
    // header so opening a panel narrows the document, never the header.
    sidebar: undefined as ComponentChildren,
    zoom: 1,
  },
  render() {
    return (
      <div className="flex h-full w-full flex-col">
        {this.show_header && (
          <ViewerHeader
            center={
              <>
                <Pager
                  max={this.pages.length}
                  min={this.pages.length ? 1 : 0}
                  on_value={n => this.on_current_page?.(n)}
                  step={this.page_grouping === "single" ? 1 : 2}
                  // The routed page starts at 0 (the 3D book's closed frame) —
                  // flat pages are 1-based, so floor the display at the first
                  // page until a scroll reports the real one.
                  value={Max(1, this.current_page)}
                />
                <Separator className="data-[orientation=vertical]:h-4" orientation="vertical" />
                <ZoomControl on_zoom={n => this.on_zoom?.(n)} zoom={this.zoom} />
              </>
            }
            left={
              <>
                <GuideToggles
                  guides={this.guides}
                  hidden={this.hidden_guides}
                  on_toggle={id => this.on_toggle_guide?.(id)}
                />
                <ToggleButton
                  name="BLEED"
                  onPressedChange={() => this.on_toggle_bleed?.()}
                  pressed={this.bleed}
                />
                {this.layout_controls}
              </>
            }
            corner={this.header_corner}
            right={this.header_actions}
          />
        )}
        <div className="flex min-h-0 flex-1">
          {this.sidebar}
          <div className="relative min-w-0 flex-1 bg-content">
            {/* A stable gutter: the reader is measured before the pages land, and
                their scrollbar would otherwise narrow it under the fit just made. */}
            <div
              className="h-full w-full overflow-auto [scrollbar-gutter:stable]"
              data-ref="reader"
              id={this.uuid}
              onScroll={this.on_scroll}
            >
              {/* Nothing until the reader is measured: the fit is unknown before
                  that, and pages drawn at 100% would overflow it for a frame. */}
              {this.container_width && this.container_height ? (
                <PlotStack
                  active_guides={this.active_guides}
                  allow_selection={this.allow_selection}
                  bleed={this.bleed}
                  center_padding={this.center_padding}
                  context={this.context}
                  group_starts={this.group_starts}
                  groups={this.groups}
                  media_count={this.media_count}
                  on_page_clicked={this.on_page_clicked}
                  printable={this.printable}
                  request_media={this.request_media}
                  stripe={this.stripe}
                  uuid={this.uuid}
                  zoom={this.effective_zoom}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    )
  },
  unmounted() {
    this.resize_observer?.disconnect()
  },
  watch: {
    current_page(n: number) {
      if (!n || n === this.detected_page) return
      this.detected_page = n
      NextFrame(() => this.scroll_to_page(n, "smooth"))
    },
    // Zooming or flipping the layout reshapes the stack, so the scroll offset
    // that used to centre the current page no longer does — pull it back to
    // centre. `effective_zoom` also covers container resizes (it's derived
    // from the container size), so this is the single re-centre path.
    animation() {
      NextFrame(() => this.scroll_to_page(this.detected_page))
    },
    // Re-pairing reshapes the stack (group widths change, so does the fit), so
    // pull the current page's new group back to centre.
    page_grouping() {
      NextFrame(() => this.scroll_to_page(this.detected_page))
    },
    effective_zoom() {
      NextFrame(() => this.scroll_to_page(this.detected_page))
    },
  },
})
